import { nanoid } from 'nanoid';
import {
  getShareALinkByShareTokenApi,
  getSurveyDetailApi,
  getSurveyLocationFilterApi,
  getSurveyDetailFiltersApi,
  getSurveyJobApi,
  getSurveyQuestionsCategoriesApi,
  getSurveySectionDetailsApi,
  getSurveyShareALinkApi,
  getSurveyVisualizationsApi,
  postSurveyVisualizationsApi,
} from '../api/surveys';
import MliVisualizationDetails from '../helpers/analytics/models/mliVisualizationDetails';
import navigationTracker from '../helpers/analytics/navigationTracker';
import types from '../types';

export const getSurveyLocationFilter = (id, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_LOCATION_FILTER_PENDING });
    const data = await getSurveyLocationFilterApi(id, jwt);
    dispatch({ type: types.reducerTypes.SURVEY_LOCATION_FILTER_SUCCESS, payload: data });
  } catch (e) {
    console.log(e);
    dispatch({ type: types.reducerTypes.SURVEY_LOCATION_FILTER_ERROR, payload: e });
  }
};

export const getSurveyDetail = (id, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_PENDING });
    const data = await getSurveyDetailApi(id, jwt);
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_SUCCESS, payload: data });
  } catch (e) {
    console.log(e);
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_ERROR, payload: e });
  }
};

export const getSurveyDetailFilters = (id, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_FILTERS_PENDING });
    const data = await getSurveyDetailFiltersApi(id, jwt);
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_FILTERS_SUCCESS, payload: data });
  } catch (e) {
    console.log(e);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_FILTERS_ERROR,
      payload: e,
    });
  }
};

export const getSurveyVisualizations = (id, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_PENDING });
    const data = await getSurveyVisualizationsApi(id, jwt);
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_SUCCESS, payload: data });
  } catch (e) {
    console.log(e);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_ERROR,
      payload: e,
    });
  }
};

export const postSurveyVisualizations =
  (id, visualization, jwt, updateVisualization = true) =>
  async (dispatch) => {
    try {
      const data = await postSurveyVisualizationsApi(id, visualization, jwt);
      if (updateVisualization) {
        dispatch({ type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_SUCCESS, payload: data });
      }
    } catch (e) {
      console.log(e);
      dispatch({
        type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_ERROR,
        payload: e,
      });
    }
  };

export const getSurveyQuestionsCategories = (id, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_QUESTIONS_CATEGORIES_PENDING });
    const data = await getSurveyQuestionsCategoriesApi(id, jwt);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_QUESTIONS_CATEGORIES_SUCCESS,
      payload: data,
    });
  } catch (e) {
    console.log(e);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_QUESTIONS_CATEGORIES_ERROR,
      payload: e,
    });
  }
};

export const getSurveySectionDetails = (id, visualization, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_SECTION_PENDING });
    const data = await getSurveySectionDetailsApi(id, visualization, jwt);
    const location = data?.headers?.location;
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_SECTION_SUCCESS,
      payload: location?.substring(location?.lastIndexOf('/') + 1),
    });
  } catch (e) {
    console.log(e);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_SECTION_ERROR,
      payload: e,
    });
  }
};

const MOE_JOB_POLL_INTERVAL_MS = 3000;
const MOE_REFRESH_INTERVAL_MS = 5000;
const MOE_TIMEOUT_MS = 60000;
// Refreshes in a row without any new MoE value before accepting the result as
// final: some points never get an MoE, so "all points filled" may never happen.
const MOE_STALL_REFRESHES = 2;

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// Starts a section query. Resolves with its job id (null if none was returned).
const startSectionJob = async (id, visualization, jwt, moe) => {
  const data = await getSurveySectionDetailsApi(id, visualization, jwt, moe);
  const location = data?.headers?.location;
  return location ? location.substring(location.lastIndexOf('/') + 1) : null;
};

// Polls a job until its result is ready. Resolves with the job result, or null
// if it did not finish before the deadline or the calculation was superseded.
const waitForSectionJob = async (jobId, jwt, deadline, isActive) => {
  if (!jobId) return null;
  while (Date.now() < deadline && isActive()) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const job = await getSurveyJobApi(jobId, jwt);
      if (job?.questions?.length) return job;
    } catch (e) {
      // The job endpoint can fail while the job is still running; keep polling.
      console.warn(e);
    }
    // eslint-disable-next-line no-await-in-loop
    await wait(MOE_JOB_POLL_INTERVAL_MS);
  }
  return null;
};

// Same rule the backend uses (ElasticsearchAdapter.isMoeCompleted): a point
// with a value but no margin of error is still pending.
const countMoe = (section) => {
  let filled = 0;
  let missing = 0;
  (section?.questions || []).forEach((question) =>
    (question.queryResult?.mliDataSegments || []).forEach((segment) =>
      (segment.mliDataSeries || []).forEach((serie) =>
        (serie.mliDataPoints || []).forEach((point) => {
          if (point.marginOfError != null) filled += 1;
          else if (point.value >= 0) missing += 1;
        })
      )
    )
  );
  return { filled, missing };
};

// The backend calculates the MoE asynchronously: the moe=true job starts the
// calculation and usually returns before it is done; the values land in the
// backend cache and only show up on a later query. So after the moe=true job,
// keep re-querying (moe=false only reads the cache, it does not recalculate)
// until the MoE is complete or stops growing, then publish the result once so
// the charts re-render a single time, with the MoE included.
//
// The "Calculating margin of error" modal only covers starting the
// calculation; the user can keep exploring while the result is collected.
export const calculateMoe = (id, visualization, jwt) => async (dispatch, getState) => {
  const requestId = nanoid();
  // Stop as soon as this calculation is superseded: another calculation,
  // section change or page left all clear/replace moeRequestId.
  const isActive = () => getState().surveyDetails.moeRequestId === requestId;
  try {
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_CALCULATE_MOE_PENDING,
      payload: { requestId },
    });
    const deadline = Date.now() + MOE_TIMEOUT_MS;

    const moeJobId = await startSectionJob(id, visualization, jwt, true);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_CALCULATE_MOE_STARTED,
      payload: { requestId },
    });
    navigationTracker.onCalculateSectionMarginOfError(
      { id, name: 'SectionName' },
      new MliVisualizationDetails(visualization.details)
    );

    let result = await waitForSectionJob(moeJobId, jwt, deadline, isActive);
    let moe = countMoe(result);
    let stalled = 0;
    while (
      moe.missing > 0 &&
      stalled < MOE_STALL_REFRESHES &&
      Date.now() + MOE_REFRESH_INTERVAL_MS < deadline &&
      isActive()
    ) {
      // eslint-disable-next-line no-await-in-loop
      await wait(MOE_REFRESH_INTERVAL_MS);
      if (!isActive()) break;
      // eslint-disable-next-line no-await-in-loop
      const jobId = await startSectionJob(id, visualization, jwt, false);
      // eslint-disable-next-line no-await-in-loop
      const next = await waitForSectionJob(jobId, jwt, deadline, isActive);
      const nextMoe = countMoe(next);
      if (next && nextMoe.filled >= moe.filled) {
        // Only count as stalled once some MoE has arrived: until then the
        // backend may simply not have finished yet.
        stalled = nextMoe.filled === moe.filled && moe.filled > 0 ? stalled + 1 : 0;
        result = next;
        moe = nextMoe;
      }
    }

    if (!moe.filled) {
      // Nothing to show: keep the current charts rather than reloading them.
      console.warn(`No margin of error was returned for section ${id}`);
    }
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_CALCULATE_MOE_SUCCESS,
      payload: { requestId, jobs: moe.filled ? result : null },
    });
  } catch (e) {
    console.error(e);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_CALCULATE_MOE_ERROR,
      payload: { requestId, message: e.message },
    });
  }
};

export const getSurveyJob = (id, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_JOB_PENDING });
    const data = await getSurveyJobApi(id, jwt);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_JOB_SUCCESS,
      payload: data,
    });
  } catch (e) {
    console.log(e);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_JOB_ERROR,
      payload: e,
    });
  }
};

export const getSurveyShareALink =
  (idVisualization, idSurvey, idActiveElem, jwt) => async (dispatch) => {
    try {
      dispatch({ type: types.reducerTypes.SHARE_A_LINK_PENDING });
      const data = await getSurveyShareALinkApi(idVisualization, idSurvey, idActiveElem, jwt);
      dispatch({ type: types.reducerTypes.SHARE_A_LINK_SUCCESS, payload: data });
    } catch (e) {
      console.log(e);
      dispatch({
        type: types.reducerTypes.SHARE_A_LINK_ERROR,
        payload: e,
      });
    }
  };

export const getSurveyShareALinkByShareToken = (sharedToken, jwt) => async (dispatch) => {
  try {
    dispatch({ type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_PENDING });
    const data = await getShareALinkByShareTokenApi(sharedToken, jwt);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_SUCCESS,
      payload: data.visualization,
    });
    dispatch({
      type: types.reducerTypes.SHARE_A_LINK_BY_TOKEN_SUCCESS,
      payload: data,
    });
  } catch (e) {
    console.log(e);
    dispatch({
      type: types.reducerTypes.SURVEY_DETAILS_VISUALIZATIONS_ERROR,
      payload: e,
    });
  }
};
